/**
 * Global Search Bar — header component
 * Type-ahead search across leads (nama_account, NPSN, alamat).
 * Triggers Leads CRM navigation + open the lead detail.
 *
 * Backend: reuses GET /api/leads?search={q}&limit=8
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Search, X, Building, MapPin } from "lucide-react";
import { API } from "../../lib/api";

export default function GlobalSearch({ onSelectLead }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Debounced search
  useEffect(() => {
    if (!q || q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/leads`, { params: { search: q.trim(), limit: 8 } });
        setResults(r.data?.rows || []);
      } catch (e) { setResults([]); }
      finally { setLoading(false); }
    }, 280);
    return () => clearTimeout(t);
  }, [q]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Keyboard shortcut: Cmd/Ctrl+K to focus
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  const select = (lead) => {
    onSelectLead?.(lead.id);
    setOpen(false);
    setQ("");
    setResults([]);
  };

  return (
    <div ref={wrapperRef} data-testid="global-search" style={{ position: "relative", flex: "0 1 280px", maxWidth: 320 }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 6, background: "var(--surface-2)",
        border: "1px solid var(--border)", borderRadius: 8, padding: "6px 10px",
      }}>
        <Search size={14} color="var(--muted)" />
        <input
          ref={inputRef}
          type="text"
          placeholder="Cari sekolah, NPSN, alamat… (⌘K)"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          data-testid="global-search-input"
          style={{ border: "none", outline: "none", background: "transparent",
                   flex: 1, color: "var(--text)", fontSize: 13 }}
        />
        {q && (
          <button onClick={() => { setQ(""); setResults([]); }}
            style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0 }}
            data-testid="global-search-clear">
            <X size={14} color="var(--muted)" />
          </button>
        )}
      </div>
      {open && q.trim().length >= 2 && (
        <div data-testid="global-search-results" style={{
          position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0,
          background: "var(--surface)", border: "1px solid var(--border)",
          borderRadius: 8, boxShadow: "0 12px 32px rgba(0,0,0,.35)",
          zIndex: 500, maxHeight: 360, overflow: "auto",
        }}>
          {loading ? (
            <div style={{ padding: 12, color: "var(--muted)", fontSize: 12 }}>Mencari...</div>
          ) : results.length === 0 ? (
            <div style={{ padding: 12, color: "var(--muted)", fontSize: 12 }}>
              Tidak ada hasil untuk &quot;{q}&quot;
            </div>
          ) : (
            results.map((r) => (
              <button
                key={r.id}
                onClick={() => select(r)}
                data-testid={`gs-result-${r.id}`}
                style={{
                  display: "flex", alignItems: "center", gap: 10, width: "100%",
                  padding: "10px 12px", background: "transparent", border: "none",
                  borderBottom: "1px solid var(--border)", cursor: "pointer",
                  textAlign: "left", color: "var(--text)",
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = "var(--surface-2)"}
                onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
              >
                <Building size={14} color="var(--accent)" style={{ flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, overflow: "hidden",
                                textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {r.nama_account || "—"}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--muted)", display: "flex",
                                alignItems: "center", gap: 4, marginTop: 2 }}>
                    {r.npsn && <span>NPSN {r.npsn}</span>}
                    {r.npsn && r.alamat_account && <span>·</span>}
                    {r.alamat_account && (
                      <span style={{ display: "flex", alignItems: "center", gap: 2,
                                     overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        <MapPin size={10} /> {r.alamat_account.slice(0, 40)}
                      </span>
                    )}
                  </div>
                </div>
                <span className="badge" style={{ fontSize: 10, flexShrink: 0 }}>{r.status_lead}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
